import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { BORKU_LOGO_PNG } from '../assets/pdf/borkuLogo'

// Page geometry (A4 portrait, millimetres).
const MARGIN = 14
const PAGE_WIDTH = 210
const RIGHT_EDGE = PAGE_WIDTH - MARGIN

const INK = [33, 33, 33]
const MUTED = [110, 110, 110]
const RULE = [220, 220, 220]
const BRAND = [244, 118, 39]

const LOGO_SIZE = 24

// Orders table geometry. The three money columns are fixed so their right edges
// are known up front — the totals block below the table is aligned to them.
const CELL_PADDING = 2.5
const MONEY_COL_WIDTH = 26
const MONEY_COLUMNS = [4, 5, 6]

// Top-right mark, sitting above the rule so it never collides with the issuer
// block beneath it. A bad image must not take the whole report down, so a
// failure here is swallowed and the page renders without the logo.
const drawLogo = doc => {
  try {
    // The alias makes both pages reference one embedded copy instead of two,
    // and compression keeps the bitmap from dominating the file size.
    doc.addImage(
      BORKU_LOGO_PNG,
      'PNG',
      RIGHT_EDGE - LOGO_SIZE,
      6,
      LOGO_SIZE,
      LOGO_SIZE,
      'borku-logo',
      'FAST'
    )
  } catch (error) {
    // Intentionally ignored: the report is still valid without the mark.
  }
}

const formatMoney = (symbol, amount) =>
  `${symbol || ''}${(Number(amount) || 0).toFixed(2)}`

// "6 Mar 2022" — the long form used in the invoice header and period line.
const formatLongDate = value => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  })
}

// "28/02/22" — the compact form used in the orders table.
const formatShortDate = value => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: 'UTC'
  })
}

// Draws a block of lines right-aligned to the page margin, returning the y the
// caller should continue from.
const drawRightBlock = (doc, lines, startY, lineHeight = 5) => {
  let y = startY
  lines.forEach(line => {
    if (!line || !line.text) return
    doc.setFont('helvetica', line.bold ? 'bold' : 'normal')
    doc.setFontSize(line.size || 9)
    doc.setTextColor(...(line.color || MUTED))
    doc.text(String(line.text), RIGHT_EDGE, y, { align: 'right' })
    y += line.gap || lineHeight
  })
  return y
}

const drawLeftBlock = (doc, lines, startY, lineHeight = 5) => {
  let y = startY
  lines.forEach(line => {
    if (!line || !line.text) return
    doc.setFont('helvetica', line.bold ? 'bold' : 'normal')
    doc.setFontSize(line.size || 9)
    doc.setTextColor(...(line.color || INK))
    doc.text(String(line.text), MARGIN, y)
    y += line.gap || lineHeight
  })
  return y
}

const periodLabel = report =>
  `${formatLongDate(report.periodStart)} - ${formatLongDate(report.periodEnd)}`

// Page one: the invoice summary — parties, period, charge lines and totals.
const renderInvoicePage = (doc, report, title) => {
  const symbol = report.currencySymbol
  const issuer = report.issuer || {}
  const recipient = report.recipient || {}

  drawLogo(doc)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(16)
  doc.setTextColor(...INK)
  doc.text(title, MARGIN, 20)

  doc.setDrawColor(...BRAND)
  doc.setLineWidth(0.8)
  doc.line(MARGIN, 24, RIGHT_EDGE, 24)

  // Invoice number, then the "bill to" party beneath it.
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(...INK)
  doc.text('Invoice no.', MARGIN, 34)
  doc.setFont('helvetica', 'normal')
  doc.text(String(report.invoiceNumber || ''), MARGIN + 22, 34)

  const recipientLines = [
    { text: recipient.name, bold: true },
    ...String(recipient.address || '')
      .split(/\r?\n|,\s*/)
      .filter(Boolean)
      .map(text => ({ text })),
    recipient.phone ? { text: recipient.phone } : null
  ].filter(Boolean)
  const leftEnd = drawLeftBlock(doc, recipientLines, 42)

  // Issuer details, right-aligned, mirroring the reference invoice.
  let rightY = drawRightBlock(
    doc,
    [{ text: issuer.companyName, bold: true, size: 10, color: INK, gap: 7 }],
    32
  )
  rightY = drawRightBlock(
    doc,
    String(issuer.companyAddress || '')
      .split(/\r?\n|,\s*/)
      .filter(Boolean)
      .map(text => ({ text })),
    rightY
  )
  rightY += 3
  rightY = drawRightBlock(
    doc,
    [
      issuer.companyPhone ? { text: `Tel ${issuer.companyPhone}` } : null,
      issuer.companyFax ? { text: `Fax ${issuer.companyFax}` } : null
    ].filter(Boolean),
    rightY
  )
  rightY += 3
  rightY = drawRightBlock(
    doc,
    [
      issuer.vatNumber ? { text: `VAT No ${issuer.vatNumber}` } : null,
      { text: `Invoice Date ${formatLongDate(report.invoiceDate)}` }
    ].filter(Boolean),
    rightY,
    6
  )

  // Period heading sits below whichever column ran longer.
  const periodY = Math.max(leftEnd, rightY) + 8
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(...INK)
  doc.text(periodLabel(report), MARGIN, periodY)

  // Charge lines.
  let y = periodY + 14
  doc.setFontSize(9)
  ;(report.charges || []).forEach(charge => {
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...MUTED)
    doc.text(String(charge.label || ''), MARGIN, y)
    doc.setTextColor(...INK)
    doc.text(formatMoney(symbol, charge.amount), RIGHT_EDGE, y, {
      align: 'right'
    })
    y += 7
  })

  // Totals block, right-aligned with a rule above the grand total.
  y += 8
  const labelX = RIGHT_EDGE - 55
  const totalRow = (label, amount, bold) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setTextColor(...(bold ? INK : MUTED))
    doc.text(label, labelX, y)
    doc.setTextColor(...INK)
    doc.text(formatMoney(symbol, amount), RIGHT_EDGE, y, { align: 'right' })
    y += 7
  }
  // No subtotal or VAT row. The charge lines above are the only components, so
  // the rule and the total carry the arithmetic between them, and VAT is
  // charged at zero. The total keeps its name because it is still VAT-inclusive.
  doc.setDrawColor(...RULE)
  doc.setLineWidth(0.4)
  doc.line(labelX, y - 4, RIGHT_EDGE, y - 4)
  y += 1
  totalRow('Total inc. VAT', report.totalIncVat, true)

  // What the store is owed. Built as a waterfall from the gross charge rather
  // than from item sales, because the deductions below include money that was
  // never part of that sales figure -- delivery, service fees and tips are
  // collected into the store's connected account and swept back out with the
  // commission as one Stripe application fee. Subtracting them from item sales
  // would double-count them and miss the payout.
  const settlement = report.settlement
  const collected = report.platformCollected
  if (settlement && collected) {
    y += 8
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(...INK)
    doc.text('Payment to you', labelX, y)
    y += 7
    doc.setFontSize(9)
    totalRow('Total charged to customers', collected.chargedTotal)
    // One line rather than a row per component: the store sees a single
    // application fee on its Stripe statement, so matching that shape is what
    // makes the two documents comparable. The note below says what it covers.
    totalRow('Less Borku fee', -Math.abs(collected.total || 0))

    doc.setDrawColor(...RULE)
    doc.setLineWidth(0.4)
    doc.line(labelX, y - 4, RIGHT_EDGE, y - 4)
    y += 1
    totalRow('Your sales for the period', settlement.payoutTotal, true)

    // Credit-paid orders take no card payment, so this part of the payout
    // cannot come through Stripe -- Borku pays it to the store directly.
    if (settlement.borkuToSettle > 0) {
      totalRow('Borku to pay you directly', settlement.borkuToSettle, true)
    }

    // Spell out what the single fee line above is made of, so the figure is not
    // just an unexplained deduction. Credits only get a mention when some were
    // actually spent, since they reduce the fee rather than adding to it.
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(...MUTED)
    const feeNote =
      'Borku fee covers commission, service fees, delivery charges and low-order fees' +
      (collected.creditApplied > 0
        ? `, less ${formatMoney(
            symbol,
            collected.creditApplied
          )} of Borku credits applied.`
        : '.')
    doc.text(feeNote, RIGHT_EDGE, y, { align: 'right' })
  }
}

// Page two: the itemised orders that make up the period.
const renderOrdersPage = (doc, report, title) => {
  const symbol = report.currencySymbol
  doc.addPage()
  drawLogo(doc)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(16)
  doc.setTextColor(...INK)
  doc.text(title, MARGIN, 20)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.text(periodLabel(report), MARGIN, 27)

  const rows = (report.orders || []).map((order, index) => [
    index + 1,
    formatShortDate(order.date),
    order.orderId || '',
    order.type || '',
    order.card ? formatMoney(symbol, order.card) : '',
    order.refund ? formatMoney(symbol, -order.refund) : '',
    formatMoney(symbol, order.total)
  ])

  autoTable(doc, {
    startY: 34,
    head: [['#', 'Date', 'Order No.', 'Type', 'Card', 'Refund', 'Total']],
    body: rows.length
      ? rows
      : [['', '', 'No orders in this period', '', '', '', '']],
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: CELL_PADDING, textColor: MUTED },
    headStyles: {
      fontStyle: 'bold',
      textColor: INK,
      lineWidth: { bottom: 0.4 },
      lineColor: RULE
    },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    columnStyles: {
      0: { cellWidth: 10 },
      4: { halign: 'right', cellWidth: MONEY_COL_WIDTH },
      5: { halign: 'right', cellWidth: MONEY_COL_WIDTH },
      6: { halign: 'right', cellWidth: MONEY_COL_WIDTH }
    },
    // columnStyles are only applied to body cells by autotable, so the money
    // headers have to be right-aligned (and sized) here to sit over their values.
    didParseCell: data => {
      if (MONEY_COLUMNS.includes(data.column.index)) {
        if (data.section === 'head') {
          data.cell.styles.halign = 'right'
          data.cell.styles.cellWidth = MONEY_COL_WIDTH
        }
      }
    },
    margin: { left: MARGIN, right: MARGIN }
  })

  // Totals block. Amounts are right-aligned to the inside edge of the Total
  // column above them, so the two read as one column down the page.
  const amountX = RIGHT_EDGE - CELL_PADDING
  const startSummaryY = (doc.lastAutoTable?.finalY || 34) + 15
  let currentY = startSummaryY

  const labelX = RIGHT_EDGE - 55
  doc.setDrawColor(...RULE)
  doc.setLineWidth(0.4)
  doc.line(labelX, currentY - 5, RIGHT_EDGE, currentY - 5)

  // Draw the summary labels and values right-aligned
  // Define the rows to display
  const summaryRows = [
    { label: 'Card', amount: report.cardTotal, bold: false },
    ...(report.refundTotal
      ? [{ label: 'Refunds', amount: -report.refundTotal, bold: false }]
      : []),
    { label: 'Total', amount: report.ordersTotal, bold: true }
  ]

  summaryRows.forEach(row => {
    doc.setFont('helvetica', row.bold ? 'bold' : 'normal')
    doc.setTextColor(...(row.bold ? INK : MUTED))
    doc.text(row.label, labelX, currentY)
    doc.setTextColor(...INK)
    doc.text(formatMoney(symbol, row.amount), amountX, currentY, {
      align: 'right'
    })
    currentY += 7
  })
}

// Builds the two-page report document. Kept separate from saving so the layout
// can be exercised without a browser.
export const buildReportDoc = (report, { subject } = {}) => {
  if (!report) return null
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const owner = report.issuer?.companyName || 'Borku'

  renderInvoicePage(doc, report, `Your ${owner} invoice`)
  renderOrdersPage(doc, report, `${subject || ''} orders`.trim())

  return doc
}

// Builds the report and hands it to the browser as a download.
export const generateReportPdf = (report, { subject, fileName }) => {
  const doc = buildReportDoc(report, { subject })
  if (!doc) return
  doc.save(fileName)
}

export default generateReportPdf
